import { useCallback, useState } from "react";

import { useDispatch } from "react-redux";

import { useTranslate } from "@similarweb/i18n-provider";

import type { IUser } from "app/@types/ISwSettings";
import { showErrorToast } from "app/actions/toast_actions";
import { swSettings } from "app/common/services/swSettings";
import { renderErrorToast } from "app/pages/sales-intelligence/common-components/ErrorToast/ErrorToast";
import { TeamMembersTable } from "app/pages/sales-intelligence/modules/teams/components/TeamMembersTable/TeamMembersTable";
import { useUpdateTeamMembersMutation } from "app/pages/sales-intelligence/modules/teams/queries/useUpdateTeamMembersMutation";
import type { TeamUser } from "app/pages/sales-intelligence/modules/teams/types/teams.types";

type Props = {
    teamId: number;
    members: readonly TeamUser[];
    onAddMembers: () => void;
};

export const TeamMembersContainer = ({ teamId, members, onAddMembers }: Props) => {
    const { translate } = useTranslate();
    const dispatch = useDispatch();

    const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);

    const { mutateAsync: updateTeamMembers, isPending: isRemoving } = useUpdateTeamMembersMutation();

    const currentUserId = (swSettings.user as Pick<IUser, "id"> | undefined)?.id;

    const handleRemoveSelected = useCallback(() => {
        if (selectedUserIds.length === 0) {
            return;
        }
        const removedSet = new Set(selectedUserIds);
        const nextUserIds = members.map((m) => m.userId).filter((id) => !removedSet.has(id));
        void (async () => {
            try {
                await updateTeamMembers({ teamId, userIds: nextUserIds });
                setSelectedUserIds([]);
            } catch {
                dispatch(
                    showErrorToast(
                        renderErrorToast({
                            content: translate("si.common.toast.error"),
                            minWidth: "auto",
                        }),
                    ),
                );
            }
        })();
    }, [updateTeamMembers, members, selectedUserIds, teamId, dispatch, translate]);

    return (
        <TeamMembersTable
            members={members}
            currentUserId={currentUserId}
            selectedUserIds={selectedUserIds}
            onSelectionChange={setSelectedUserIds}
            onAddMembers={onAddMembers}
            onRemoveSelected={handleRemoveSelected}
            isRemoving={isRemoving}
        />
    );
};
